<template>
    <div class="relative aspect-video">
        <iframe
            id="easyPlayerIframe"
            width="100%"
            height="100%"
            class="absolute top-0 left-0 w-full h-full wistia_embed"
            allow="autoplay; fullscreen"
            :src="embedFileSrc"
        ></iframe>
    </div>
</template>

<script>
import { mapGetters } from "vuex";
import { PLAYER_FILE_TYPES, PLAYER_VIDEO_SOURCES } from "@/apps/player/services/players.service";

const youtubeVideoRegex =
    // eslint-disable-next-line no-useless-escape
    /^(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:embed\/|v\/|watch\?v=|embed\/videoseries\?list=)|youtu\.be\/)([^\s&?\/#]+)/;

const vimeoVideoRegex =
    // eslint-disable-next-line no-useless-escape
    /^(?:https?:\/\/)?(?:www\.)?(?:vimeo\.com\/|player\.vimeo\.com\/video\/)([0-9]+)/;

const wistiaVideoRegex =
    // eslint-disable-next-line no-useless-escape
    /^(?:https?:\/\/)?(?:www\.)?(?:embed\.wistia\.com\/deliveries\/|(.+)?(wistia\.com|wi\.st)\/(?:medias|embed)\/)([^\s&?\/#]+)/;

export default {
    name: "FileEmbed",
    computed: {
        ...mapGetters("player/view", ["currentFile"]),
        currentFileSrc() {
            return this.currentFile ? this.currentFile.src : "";
        },
        isYoutube() {
            return (
                (this.currentFile.type === PLAYER_FILE_TYPES.video &&
                    this.currentFile.videoSrc === PLAYER_VIDEO_SOURCES.youtube) ||
                (this.currentFile.type === PLAYER_FILE_TYPES.embed &&
                    !!this.currentFileSrc.match(youtubeVideoRegex))
            );
        },
        isVimeo() {
            return (
                (this.currentFile.type === PLAYER_FILE_TYPES.video &&
                    this.currentFile.videoSrc === PLAYER_VIDEO_SOURCES.vimeo) ||
                (this.currentFile.type === PLAYER_FILE_TYPES.embed &&
                    !!this.currentFileSrc.match(vimeoVideoRegex))
            );
        },
        isWistia() {
            return (
                (this.currentFile.type === PLAYER_FILE_TYPES.video &&
                    this.currentFile.videoSrc === PLAYER_VIDEO_SOURCES.wistia) ||
                (this.currentFile.type === PLAYER_FILE_TYPES.embed &&
                    !!this.currentFileSrc.match(wistiaVideoRegex))
            );
        },
        embedFileSrc() {
            if (this.isYoutube) {
                const youtubeMatches =
                    /(youtu.*be.*)\/(watch\?v=|embed\/|v|shorts|)(.*?((?=[&#?])|$))/gm.exec(
                        this.currentFileSrc
                    );
                const youtubeVideoId = youtubeMatches ? youtubeMatches[3] : null;

                if (youtubeVideoId) {
                    return `https://www.youtube.com/embed/${youtubeVideoId}?enablejsapi=1&version=3&origin=${window.location.origin}`;
                }
                return this.currentFileSrc;
            }
            if (this.isVimeo) {
                const vimeoMatches = /(vimeo.*com.*)\/(video\/|)(.*?((?=[&#?])|$))/gm.exec(
                    this.currentFileSrc
                );
                const vimeoVideoId = vimeoMatches ? vimeoMatches[3] : null;

                if (vimeoVideoId) {
                    return `https://player.vimeo.com/video/${vimeoVideoId}?api=1&loop=0&autoplay1`;
                }
                return this.currentFileSrc;
            }
            return this.currentFileSrc;
        },
    },
    watch: {
        currentFileSrc: {
            immediate: true,
            handler(newSrc, oldSrc) {
                if (newSrc !== oldSrc) {
                    this.turnOnProperPlayer();
                }
            },
        },
        currentFile: {
            deep: true,
            handler(newFile, oldFile) {
                if (newFile.videoSrc !== oldFile.videoSrc) {
                    this.turnOnProperPlayer();
                }
            },
        },
    },
    methods: {
        createYoutubePlayer() {
            new window.YT.Player("easyPlayerIframe", {
                playerVars: {
                    showInfo: 0,
                    modestbranding: 1,
                    autoplay: 1,
                    rel: 0,
                },
                events: {
                    onStateChange: window.onYoutubePlayerStateChange,
                },
            });
        },
        turnOnYoutube() {
            if (window.YT && window.onYoutubePlayerStateChange) {
                this.createYoutubePlayer();
            } else {
                const tag = document.createElement("script");
                tag.src = "https://www.youtube.com/player_api";
                const firstScriptTag = document.getElementsByTagName("script")[0];
                firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);

                window.onYouTubeIframeAPIReady = this.createYoutubePlayer;

                window.onYoutubePlayerStateChange = function (event) {
                    if (event.data == window.YT.PlayerState.ENDED) {
                        const finishLessonEvent = new Event("finishLesson");
                        document.dispatchEvent(finishLessonEvent);
                    }
                };
            }
        },
        createVimeoPlayer() {
            const player = new window.Vimeo.Player("easyPlayerIframe");
            player.on("ended", function () {
                const finishLessonEvent = new Event("finishLesson");
                document.dispatchEvent(finishLessonEvent);
            });
        },
        turnOnVimeo() {
            if (window.Vimeo) {
                this.createVimeoPlayer();
            } else {
                const tag = document.createElement("script");
                tag.src = "https://player.vimeo.com/api/player.js";
                const firstScriptTag = document.getElementsByTagName("script")[0];
                firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);

                tag.addEventListener("load", window.vimeoReady);

                window.vimeoReady = this.createVimeoPlayer;
            }
        },
        turnOnWistia() {
            const tag = document.createElement("script");
            tag.src = "//fast.wistia.net/assets/external/E-v1.js";
            const firstScriptTag = document.getElementsByTagName("script")[0];
            firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);

            //wait until loaded
            tag.addEventListener("load", wistiaReady);

            function wistiaReady() {
                window._wq = window._wq || [];
                window._wq.push({
                    id: "_all",
                    onReady: function (video) {
                        video.bind("end", function () {
                            const finishLessonEvent = new Event("finishLesson");
                            document.dispatchEvent(finishLessonEvent);
                        });
                    },
                });
            }
        },
        turnOnProperPlayer() {
            if (this.isYoutube) {
                this.$nextTick(() => {
                    this.turnOnYoutube();
                });
            }
            if (this.isVimeo) {
                this.$nextTick(() => {
                    this.turnOnVimeo();
                });
            }
            if (this.isWistia) {
                this.$nextTick(() => {
                    this.turnOnWistia();
                });
            }
        },
    },
};
</script>
